import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customerAuthApi } from "@/lib/customerAuth";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

/**
 * Handles the return leg of Emergent Google Auth.
 * URL fragment shape: `/#session_id=<opaque>` (or any route with that hash).
 * Runs ONCE, exchanges the session_id for our cookie, then routes forward.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useCustomerAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Where to go afterwards — the path before the fragment, or "/".
    const returnTo = location.pathname && location.pathname !== "/customer/login"
      ? location.pathname
      : "/";

    if (!sessionId) {
      nav(returnTo, { replace: true });
      return;
    }
    customerAuthApi.google(sessionId)
      .then((me) => {
        setUser(me);
        window.history.replaceState(null, "", returnTo);
        nav(returnTo, { replace: true });
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error?.message ||
          err?.response?.data?.detail ||
          "Sign-in didn't complete. Please try again.";
        // Wipe the fragment so a refresh doesn't reprocess the (spent) session_id.
        window.history.replaceState(null, "", "/customer/login");
        nav("/customer/login", { replace: true, state: { authError: msg, from: returnTo } });
      });
  }, [location, nav, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-background text-sm text-muted-foreground" data-testid="auth-callback">
      Signing you in…
    </div>
  );
}
